import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import axios, { pluckData } from 'src/utilities/http-client'

import { AppRootState, Status } from 'src/store/store'
import { TourCompletion } from './types'

type ToursState = {
  completions: Record<string, TourCompletion>
  completionsStatus: Status
  activeTourID: string | null
  hasAutoPlayedThisSession: boolean
}

export let initialState: ToursState = {
  completions: {},
  completionsStatus: 'uninitialized',
  activeTourID: null,
  hasAutoPlayedThisSession: false,
}

export let fetchTourCompletions = createAsyncThunk(
  'tours/fetchTourCompletions',
  () => axios.get<TourCompletion[]>('/api/tours/completions').then(pluckData),
)

export let saveTourCompletion = createAsyncThunk(
  'tours/saveTourCompletion',
  (payload: { tourID: string; completedVersion: number }) =>
    axios
      .post<TourCompletion>('/api/tours/completions', payload)
      .then(pluckData),
)

export let toursSlice = createSlice({
  name: 'tours',
  initialState,
  reducers: {
    tourStarted: (state, action: PayloadAction<string>) => {
      // Don't override a tour that's already running. On the self-paced page
      // useAutoTour and usePageTour can both dispatch a start in the same commit
      // (each reads a stale activeTourID of null); first writer wins so we never
      // start two tours back-to-back or clobber the auto-tour.
      if (state.activeTourID) return
      state.activeTourID = action.payload
    },
    autoTourStarted: (state, action: PayloadAction<string>) => {
      if (state.activeTourID) return
      state.activeTourID = action.payload
      state.hasAutoPlayedThisSession = true
    },
    activeTourCleared: (state) => {
      state.activeTourID = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTourCompletions.pending, (state) => {
        state.completionsStatus = 'pending'
      })
      .addCase(fetchTourCompletions.rejected, (state) => {
        state.completionsStatus = 'rejected'
      })
      .addCase(fetchTourCompletions.fulfilled, (state, action) => {
        state.completionsStatus = 'fulfilled'
        state.completions = action.payload.reduce(
          (acc, c) => ({ ...acc, [c.tourID]: c }),
          {} as Record<string, TourCompletion>,
        )
      })
      .addCase(saveTourCompletion.fulfilled, (state, action) => {
        let { tourID, completedVersion, completedAt } = action.payload
        state.completions[tourID] = { tourID, completedVersion, completedAt }
      })
  },
})

export let { tourStarted, autoTourStarted, activeTourCleared } =
  toursSlice.actions

export let selectTourCompletions = (state: AppRootState) =>
  state.tours.completions
export let selectTourCompletionsStatus = (state: AppRootState) =>
  state.tours.completionsStatus
export let selectActiveTourID = (state: AppRootState) =>
  state.tours.activeTourID
export let selectTourCompletion = (tourID: string) => (state: AppRootState) =>
  state.tours.completions[tourID]
export let selectTourIsUpdated =
  (tourID: string, currentVersion: number) => (state: AppRootState) => {
    let completion = state.tours.completions[tourID]
    return !!completion && completion.completedVersion < currentVersion
  }
export let selectHasAutoPlayedThisSession = (state: AppRootState) =>
  state.tours.hasAutoPlayedThisSession

export default toursSlice.reducer
